import React from 'react';
import { useTranslation } from 'react-i18next';

import { store } from '@/app/store';
import { RoleContext } from '@/app/api/common';
import { ScloneCasting, ScloneEntry, MAX_UPLOAD_BYTES, isLegacySclonesBackend } from '@/features/sclones/sclonesSlice';

// Hooks that own the clone-settings (sClone) data + permission logic, keeping
// the CloneSettings page purely presentational. The catalog is global and
// small, so it lives in local state here and is refetched after each mutation.

export interface UploadProgress {
  loaded: number;
  total: number;
}

export interface SclonePermissions {
  canView:   boolean;
  canChange: boolean;
  canDelete: boolean;
}

// Mirror the server gate as UX (the server is the source of truth and 403s
// regardless). List also accepts SeeAllProjects as an alternative to
// ViewSclones.
export function useSclonePermissions(): SclonePermissions {
  const role = React.useContext(RoleContext);
  return React.useMemo(() => ({
    canView:   role.admin || !!role?.privilages?.includes("ViewSclones") || !!role?.privilages?.includes("SeeAllProjects"),
    canChange: role.admin || !!role?.privilages?.includes("ChangeSclones"),
    canDelete: role.admin || !!role?.privilages?.includes("DeleteSclones"),
  }), [role]);
}

export interface UseSclones {
  entries:       ScloneEntry[];
  // The backend has no casting support (see isLegacySclonesBackend).
  legacyBackend: boolean;
  uploading:     boolean;
  progress:      UploadProgress | null;
  refresh:       () => Promise<void>;
  // Early size check, so an oversized zip fails before the casting choice.
  checkFile:     (file: File) => boolean;
  // casting null only on a legacy backend (no casting support there).
  upload:        (file: File, casting: ScloneCasting | null) => Promise<boolean>;
  remove:        (entry: ScloneEntry) => Promise<boolean>;
}

export function useSclones(): UseSclones {
  const { t } = useTranslation();

  const [entries, setEntries]     = React.useState<ScloneEntry[]>([]);
  const [uploading, setUploading] = React.useState(false);
  const [progress, setProgress]   = React.useState<UploadProgress | null>(null);

  const refresh = React.useCallback(async () => {
    setEntries(await store.getSclones());
  }, []);

  React.useEffect(() => { refresh(); }, [refresh]);

  const legacyBackend = React.useMemo(() => isLegacySclonesBackend(entries), [entries]);

  const checkFile = React.useCallback((file: File): boolean => {
    if (file.size > MAX_UPLOAD_BYTES) {
      store.dispatchError(t("clone_settings:upload:too_large"));
      return false;
    }
    return true;
  }, [t]);

  const upload = React.useCallback(async (file: File, casting: ScloneCasting | null): Promise<boolean> => {
    if (!checkFile(file)) return false;
    setUploading(true);
    setProgress({ loaded: 0, total: file.size });
    try {
      // uploadSclone already surfaces the success/error toast; on success we
      // just refetch. On failure the toast explains why (bad zip, missing
      // ProductCloneInfo.txt, invalid model/version/casting, size limit, …).
      const res = await store.uploadSclone(file, casting, (loaded, total) => setProgress({ loaded, total }));
      if (res.ok) await refresh();
      return res.ok;
    } finally {
      setUploading(false);
      setProgress(null);
    }
  }, [refresh, checkFile]);

  const remove = React.useCallback(async (entry: ScloneEntry): Promise<boolean> => {
    // Send the casting exactly as listed; null ("Both", or a legacy entry
    // without the member) leaves it out of the request.
    const ok = await store.deleteSclone(entry.model, entry.version, entry.casting ?? null);
    if (ok) await refresh();
    return ok;
  }, [refresh]);

  return { entries, legacyBackend, uploading, progress, refresh, checkFile, upload, remove };
}
